import update from "immutability-helper";
import type { AnyAction } from "redux";
import type { Project, Story } from "@theirstory/common/api_types/backend";

import {
  PROJECTS_RESET_CURRENT,
  PROJECTS_LOADED,
  PROJECT_DETAILS_LOADED,
  PROJECT_STORIES_LOADED,
  PROJECT_STORIES_LOADING,
  PROJECT_STORIES_SET_PAGE_VIEW,
  PROJECT_TABLE_STORIES_LOADED,
  PROJECT_TABLE_STORIES_LOADING,
  PROJECT_TABLE_STORIES_SET_PAGE_VIEW,
  PROJECT_STORIES_PARTIAL_UPDATE,
  PROJECT_TABLE_STORY_UPDATE,
  STORIES_ONE_UPDATED,
  PROJECT_STORIES_SORT_SETTING,
  PROJECTS_LOADING,
  PROJECTS_RESET_USER_PROJECTS,
  PROJECTS_RESET_STORIES,
} from "../actions/types";
import type { ProjectsLoaded } from "../actions/projects";

export interface ProjectsState {
  isLoadingProjects: boolean;
  userProjects: Project[];
  project: {
    created_at: string;
    projectId: string;
    projectDetails: any;
    stories: Story[];
    paging: {
      size: number;
      current: number;
      count: number;
      totalItems: number;
    };
    sort: {
      column: string;
      order: "asc" | "desc";
    };
    searchParam: string;
    isLoading: boolean;
    table: {
      stories: Story[];
      paging: {
        size: number;
        current: number;
        count: number;
        totalItems: number;
      };
      isLoading: boolean;
    };
  };
}

const initialState: ProjectsState = {
  isLoadingProjects: false,
  userProjects: [],
  project: {
    projectId: null,
    created_at: null,
    projectDetails: {},
    stories: [],
    paging: {
      size: 15,
      current: 1,
      count: 1,
      totalItems: 0,
    },
    sort: {
      column: null,
      order: null,
    },
    searchParam: null,
    isLoading: false,
    table: {
      stories: [],
      paging: {
        size: 25,
        current: 1,
        count: 1,
        totalItems: 0,
      },
      isLoading: false,
    },
  },
};

const projectsReducer = (state = initialState, { type, payload }: AnyAction) => {
  switch (type) {
    case PROJECTS_LOADING: {
      return update(state, { isLoadingProjects: { $set: true } });
    }
    case PROJECTS_LOADED: {
      return update(state, { userProjects: { $set: payload as ProjectsLoaded }, isLoadingProjects: { $set: false } });
    }
    case PROJECT_DETAILS_LOADED:
      return update(state, {
        project: {
          projectDetails: { $set: payload.projectDetails },
        },
      });
    case PROJECT_STORIES_LOADING:
      return update(state, {
        project: {
          isLoading: { $set: true },
        },
      });
    case PROJECT_STORIES_LOADED:
      return update(state, {
        project: {
          isLoading: { $set: false },
          stories: { $set: payload.stories },
          projectId: { $set: payload.projectId },
          paging: {
            current: { $set: payload.page },
            count: { $set: Math.ceil(payload.total / state.project.paging.size) },
            totalItems: { $set: payload.total },
          },
        },
      });
    case PROJECT_STORIES_PARTIAL_UPDATE:
      return update(state, {
        project: {
          stories: (s) => s.map((i) => (i._id !== payload._id ? i : update(i, { $merge: payload }))),
        },
      });
    case STORIES_ONE_UPDATED:
      return update(state, {
        project: {
          stories: (s) => s.map((i) => (i._id !== payload._id ? i : payload)),
        },
      });
    case PROJECT_STORIES_SET_PAGE_VIEW: {
      const { currentPage, pageSize } = payload;
      return update(
        state,
        pageSize != null
          ? {
              project: {
                paging: {
                  size: { $set: pageSize },
                  current: { $set: currentPage != null ? currentPage : 1 },
                },
              },
            }
          : currentPage == null
          ? {}
          : { project: { paging: { current: { $set: currentPage } } } }
      );
    }
    case PROJECT_TABLE_STORIES_LOADING:
      return update(state, {
        project: {
          table: {
            isLoading: { $set: true },
            stories: payload?.clear ? { $set: [] } : {},
          },
        },
      });
    case PROJECT_TABLE_STORIES_LOADED:
      return update(state, {
        project: {
          table: {
            isLoading: { $set: false },
            stories: { $set: payload.stories },
            paging: {
              current: { $set: payload.page },
              count: { $set: Math.ceil(payload.total / state.project.table.paging.size) },
              totalItems: { $set: payload.total },
            },
          },
        },
      });
    case PROJECT_TABLE_STORY_UPDATE:
      return update(state, {
        project: {
          table: {
            stories: (s) => s.map((i) => (i._id !== payload._id ? i : payload)),
          },
        },
      });
    case PROJECT_TABLE_STORIES_SET_PAGE_VIEW: {
      const { currentPage, pageSize } = payload;
      return update(
        state,
        pageSize != null
          ? {
              project: {
                table: {
                  paging: {
                    size: { $set: pageSize },
                    current: { $set: currentPage != null ? currentPage : 1 },
                  },
                },
              },
            }
          : currentPage == null
          ? {}
          : {
              project: {
                table: {
                  paging: {
                    current: { $set: currentPage },
                  },
                },
              },
            }
      );
    }
    case PROJECT_STORIES_SORT_SETTING: {
      const { column, order } = payload;
      return update(state, {
        project: {
          sort: {
            column: { $set: column },
            order: { $set: order },
          },
        },
      });
    }
    case PROJECTS_RESET_CURRENT: {
      return update(state, {
        project: { $set: initialState.project },
      });
    }
    case PROJECTS_RESET_USER_PROJECTS: {
      return update(state, {
        userProjects: { $set: initialState.userProjects },
      });
    }
    case PROJECTS_RESET_STORIES: {
      return update(state, {
        project: {
          stories: { $set: initialState.project.stories },
        },
      });
    }
    default:
      return state;
  }
};

export default projectsReducer;
